<template>
    <div class="container">
        <div class="page-content">
            <div class="status-sidebar card bg-black text-white mb-3 p-2">
                <div class="mini-status text-center">
                    <span class="mini turn-label" :class="currentAttacker===player ? 'you' : 'cpu'">{{ currentAttacker === player ? 'Your turn' : 'CPU turn' }}</span>
                </div>

                <div v-if="currentAttacker===player">
                    <button class="btn btn-sm btn-danger mt-2 w-100 px-0" :disabled="isResolving" @click="endTurn()">End Turn</button>
                </div>
            </div>

            <div class="row">
                <div class="col-12 col-md-9 game-display mt-3">
                    <div class="hand enemy">
                        <div class="hand-cards enemy overlapped">
                            <div v-for="(n, idx) in opponent.handCards.length"
                                 :key="'eh'+idx"
                                 class="hand-card card-back"
                                 title="Opponent hand card"></div>
                        </div>
                    </div>

                    <div :class="'row '+['player-area','enemy', currentAttacker===opponent ? 'active-turn' : '']">
                        <div class="col-3 deck-wrapper d-flex flex-column align-items-center">
                            <div class="deck">
                                <p class="placeholder-text" v-if="!opponent.deck.length">Card<br/>Deck</p>
                            </div>

                            <div class="prize-dots mt-2">
                                <span v-for="i in 3" :key="'op-prize-'+i" :class="'dot' + (i <= opponent.prizeCards.length ? ' filled' : '')"></span>
                            </div>
                        </div>

                        <div class="col-6 bank">
                            <div v-for="i in 3" :key="'op-bench-'+i" class="bank-card" :style="opponent.bank[i-1] ? ('background-image: url(' + CardService.getCardImageUrl(opponent.bank[i-1]) + ')') : ''">
                                <p class="placeholder-text" v-if="!opponent.bank[i-1]">Bankslot<br/>{{ i }}</p>
                            </div>
                        </div>

                        <div class="col-3 discard-pile-wrapper">
                            <div class="discard-pile">
                                <p class="placeholder-text" v-if="!opponent.discardPile.length">Discard<br/>Pile</p>
                            </div>
                        </div>

                        <div class="col-3 prize-cards-wrapper">
                            <div class="prize-card">
                                <p class="placeholder-text" v-if="!opponent.prizeCards.length">Prize<br/>Cards</p>
                            </div>
                        </div>

                        <div class="col-12 active-card-wrapper">
                            <div class="active-card">
                                <p class="placeholder-text" v-if="!opponent.activeCard">Active<br/>Card</p>
                                <div v-else class="card-with-overlay">
                                    <img :src="CardService.getCardImageUrl(opponent.activeCard)" alt="" class="w-100">
                                    <div class="hp-overlay" v-if="opponent.activeCard">
                                        <div class="hp-number">{{ hpRemaining(opponent.activeCard) }}</div>
                                        <div class="hp-bar">
                                            <div class="hp-fill" :style="{ width: hpPercent(opponent.activeCard) + '%' }"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div :class="'row '+['player-area','player', currentAttacker===player ? 'active-turn' : '']">
                        <div class="col-12 active-card-wrapper">
                            <div class="active-card"
                                 data-droptarget="active"
                                 @dragover.prevent
                                 @drop="onGameDropTo('active')"
                                 @click="onGameTapTarget('active')">
                                <p class="placeholder-text" v-if="!player.activeCard">
                                    Active<br/>Card
                                </p>

                                <div v-else class="card-with-overlay" v-on:click="showActiveCardActions()">
                                    <img :src="CardService.getCardImageUrl(player.activeCard)" alt="" class="w-100">

                                    <div class="hp-overlay" v-if="player.activeCard">
                                        <div class="hp-number">{{ hpRemaining(player.activeCard) }}</div>

                                        <div class="hp-bar">
                                            <div class="hp-fill" :style="{ width: hpPercent(player.activeCard) + '%' }"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-3 deck-wrapper d-flex flex-column align-items-center">
                            <div class="deck">
                                <p class="placeholder-text" v-if="!player.deck.length">Card<br/>Deck</p>
                            </div>
                            <div class="prize-dots bottom">
                                <span v-for="i in 3" :key="'pl-prize-'+i" :class="'dot' + (i <= player.prizeCards.length ? ' filled' : '')"></span>
                            </div>
                        </div>

                        <div class="col-6 bank">
                            <div v-for="i in 3" :key="'pl-bench-'+i" class="bank-card"
                                 :style="player.bank[i-1] ? ('background-image: url(' + CardService.getCardImageUrl(player.bank[i-1]) + ')') : ''"
                                 :data-droptarget="'bench'"
                                 :data-bench-index="i-1"
                                 @dragover.prevent
                                 @drop="onGameDropTo('bench', i-1)"
                                 @click="onGameTapTarget('bench', i-1)">
                                <p class="placeholder-text" v-if="!player.bank[i-1]">Bankslot<br/>{{ i }}</p>
                            </div>
                        </div>

                        <div class="col-3 discard-pile-wrapper">
                            <div class="discard-pile">
                                <p class="placeholder-text" v-if="!player.discardPile.length">Discard<br/>Pile</p>
                            </div>
                        </div>
                    </div>

                    <div class="hand player">
                        <div class="hand-cards player overlapped">
                            <img v-for="(card, idx) in player.handCards"
                                 :key="'ph'+idx"
                                 :src="CardService.getSmallCardImageUrl(card)"
                                 :alt="card.name"
                                 class="hand-card"
                                 :style="{ opacity: draggedHandIndex === idx ? 0 : 1 }"
                                 :title="card.name"
                                 draggable="false"
                                 @mousedown.prevent="startDrag(card, idx, $event)"
                                 @touchstart.prevent="startDrag(card, idx, $event)"
                                 @contextmenu.prevent="openLightbox(card)"
                                 @click="showCard(card)">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div id="card-action-view" v-if="cardToShow" v-on:click="cardToShow = null">
            <img :src="CardService.getCardImageUrl(cardToShow)" alt="" class="actions-card-image rounded">

            <div v-for="action in cardToShow.attacks" :class="'card-action'+(canPayCost(player.activeCard, action.cost || []) ? '' : ' disabled-action')">
                <div class="action-energy-cost">
                    <img v-for="energy in action.cost ?? []" :src="CardService.getTypeIcon(energy)" alt="">
                </div>

                <div class="action-info" @click="canPayCost(player.activeCard, action.cost || []) && currentPhase==='attack' ? executeAction(action) : null">
                    <span class="action-damage" v-text="action.damage"></span>
                    <span class="action-name" v-text="action.name"></span><br/>
                    <span class="action-text" v-text="action.text"></span>
                </div>
            </div>
        </div>

        <!-- Simple Lightbox for right-click on hand cards -->
        <div v-if="lightboxCard" class="lightbox" @click="lightboxCard=null">
            <img :src="CardService.getCardImageUrl(lightboxCard)" alt="" class="lightbox-image">
        </div>
    </div>

    <!-- Floating drag ghost: show whole card following cursor/finger -->
    <div v-if="isDragging && drag.card" class="drag-ghost" :style="{ left: (drag.x)+'px', top: (drag.y)+'px' }">
        <img :src="CardService.getCardImageUrl(drag.card)" :alt="drag.card?.name || 'card'"/>
    </div>
</template>

<script>
import axios from 'axios';
import iziToast from 'izitoast';
import 'izitoast/dist/css/iziToast.min.css';
import CardService from "../../CardService";

export default {
    name: "CardFight",
    data() {
        return {
            round: 0,
            turnCount: 1,
            currentPhase: 'start', // start, main, attack, end
            flags: {
                energyAttachedThisTurn: false,
                retreatedThisTurn: false,
            },
            // Drag UX state
            isDragging: false,
            drag: {card: null, index: null, x: 0, y: 0, target: null},
            lastHighlightEl: null,
            player: {
                name: 'Player',
                activeCard: null,
                bank: [],
                deck: [],
                prizeCards: [],
                handCards: [],
                discardPile: [],
                usedVMAX: false
            },
            opponent: {
                name: 'CPU',
                activeCard: null,
                bank: [],
                deck: [],
                prizeCards: [],
                handCards: [],
                discardPile: [],
                usedVMAX: false
            },
            stadiumCard: null,
            cardToShow: null,
            currentAttacker: null, // the one attacking
            currentDefender: null, // the current opponent
            isResolving: false,
            startingPlayer: null, // side who goes first (cannot attack on first turn)
            // Manual setup state
            showSetupModal: false,
            setupSelectedActive: null,
            setupSelectedBenchIds: [],
            setupSelectedBenchCards: [],
            setupDrag: {card: null, index: null, source: null},
            playerMulligans: 0,
            opponentMulligans: 0,
            // Gameplay DnD
            dragPayload: {card: null, from: null, index: null},
            touchSelect: null, // mobile touch selection from hand
            // UI state for fanned hand & lightbox
            hoveredHandIndex: -1,
            lightboxCard: null,
            draggedHandIndex: -1,
            touchAnimBusy: false,
        };
    },
    async mounted() {
        this.$store.commit('updateUserData');
        // Prevent page scrolling on CardFight
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        try {
            const decksRes = await axios.get('/api/user-decks');
            const decks = decksRes.data || [];

            if (!Array.isArray(decks) || decks.length === 0) {
                this.announce('No user decks found. Create a deck first.', 'error');
                return;
            }

            // Pick randomized user decks for testing (distinct when possible)
            const idxPlayer = Math.floor(Math.random() * decks.length);
            let idxOpponent = idxPlayer;
            if (decks.length > 1) {
                while (idxOpponent === idxPlayer) {
                    idxOpponent = Math.floor(Math.random() * decks.length);
                }
            }

            const playerDeckId = decks[idxPlayer]?.id;
            const opponentDeckId = decks[idxOpponent]?.id ?? playerDeckId;

            const [p, o] = await Promise.all([
                this.loadUserDeck(playerDeckId),
                this.loadUserDeck(opponentDeckId),
            ]);

            if (!p || !o) {
                this.announce('Could not load randomized user decks.', 'error');
                return;
            }

            // Initialize with manual start: do not auto-seed; open setup after drawing hands
            await this.setupGameFromDecks(p, o);
            // Turn will start after player confirms setup
        } catch (e) {
            console.error(e);
            this.announce('Failed to initialize CardFight from randomized user decks.', 'error');
        }
    },
    unmounted() {
        // Restore page scrolling when leaving CardFight
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
    },
    computed: {
        CardService() {
            return CardService
        },
        nextPhaseLabel() {
            if (this.currentPhase === 'main') return 'Go to Attack';
            if (this.currentPhase === 'attack') return 'End Attack';
            return 'Advance';
        }
    },
    watch: {},
    methods: {
        getEventXY(e) {
            const t = e.touches && e.touches[0] ? e.touches[0] : e;
            return {x: t.clientX, y: t.clientY};
        },
        startDrag(card, idx, e) {
            // Only allow dragging in the player's main phase
            if (this.currentAttacker !== this.player || this.currentPhase !== 'main') {
                return;
            }

            this.isDragging = true;
            this.draggedHandIndex = idx;
            const {x, y} = this.getEventXY(e);
            this.drag = {card, index: idx, x, y, target: null};
            // Also maintain legacy payload so onGameDropTo can reuse
            this.dragPayload = {card, from: 'hand', index: idx};

            // Prevent accidental text selection/scroll while dragging
            document.body.style.userSelect = 'none';
            document.body.style.touchAction = 'none';

            // Add listeners
            window.addEventListener('mousemove', this.moveDrag, {passive: true});
            window.addEventListener('mouseup', this.endDrag, {passive: true});
            window.addEventListener('touchmove', this.moveDrag, {passive: true});
            window.addEventListener('touchend', this.endDrag, {passive: true});
        },

        moveDrag(e) {
            if (!this.isDragging) return;
            const {x, y} = this.getEventXY(e);
            this.drag.x = x;
            this.drag.y = y;
            // Hit test
            const target = this.hitTest(x, y);
            this.drag.target = target;
            this.setHighlight(target?.el || null);
        },

        endDrag(e) {
            if (!this.isDragging) {
                return;
            }

            const target = this.drag.target;

            // Clear listeners
            window.removeEventListener('mousemove', this.moveDrag);
            window.removeEventListener('mouseup', this.endDrag);
            window.removeEventListener('touchmove', this.moveDrag);
            window.removeEventListener('touchend', this.endDrag);

            // Remove highlight
            this.setHighlight(null);
            this.isDragging = false;

            if (target && target.type) {
                // Commit drop via existing logic
                this.onGameDropTo(target.type, target.index ?? null);
            }

            // Reset drag payload
            this.drag = {card: null, index: null, x: 0, y: 0, target: null};
            this.dragPayload = {card: null, from: null, index: null};
            this.draggedHandIndex = -1;

            // Restore body interaction
            document.body.style.userSelect = '';
            document.body.style.touchAction = '';
        },

        hitTest(x, y) {
            const els = document.elementsFromPoint(x, y) || [];
            for (const el of els) {
                if (!(el instanceof HTMLElement)) {
                    continue;
                }

                const t = el.getAttribute('data-droptarget');

                if (t === 'active') {
                    return {el, type: 'active'};
                }

                if (t === 'bench') {
                    const idxAttr = el.getAttribute('data-bench-index');
                    const idx = idxAttr != null ? parseInt(idxAttr, 10) : null;
                    return {el, type: 'bench', index: isNaN(idx) ? null : idx};
                }
            }

            return null;
        },

        setHighlight(el) {
            if (this.lastHighlightEl === el) return;
            if (this.lastHighlightEl) this.lastHighlightEl.classList.remove('drop-highlight');
            this.lastHighlightEl = el;
            if (el) el.classList.add('drop-highlight');
        },

        hpRemaining(card) {
            const maxHp = parseInt(card?.hp, 10) || 0;
            const taken = (card?.damageMarkers || 0) * 10;
            return Math.max(maxHp - taken, 0);
        },

        hpPercent(card) {
            const maxHp = parseInt(card?.hp, 10) || 0;
            if (maxHp <= 0) return 0;
            return Math.max(0, Math.min(100, Math.round((this.hpRemaining(card) / maxHp) * 100)));
        },

        // === Turn & rules helpers ===
        resetTurnFlags() {
            this.flags.energyAttachedThisTurn = false;
            this.flags.retreatedThisTurn = false;
        },

        startTurn() {
            // Automatic draw at the beginning of the turn
            const drawn = this.drawOne(this.currentAttacker);

            if (drawn) {
                this.announce(this.currentAttacker.name + ' drew a card', 'info');
            }

            // Move directly into main phase after drawing
            this.currentPhase = 'main';

            // If it's CPU's turn, automate it
            if (this.currentAttacker === this.opponent) {
                // Schedule to ensure UI updates first
                setTimeout(() => this.cpuTakeTurn(), 250);
            }
        },

        endTurn() {
            if (this.isResolving) return;
            // End-of-turn status like poison/burn can be applied here later
            // Swap players
            const atk = this.currentAttacker;
            this.currentAttacker = this.currentDefender;
            this.currentDefender = atk;
            this.turnCount += 1;
            this.resetTurnFlags();
            this.startTurn();
        },

        drawOne(side) {
            if (!side?.deck?.length) return null;
            const card = side.deck.shift();
            side.handCards.push(card);
            return card;
        },

        normalizeEnergySymbol(sym) {
            if (!sym) return null;
            const s = String(sym).toLowerCase();
            if (s === 'colorless' || s === 'c') return 'colorless';
            return s; // e.g., 'water', 'fire', 'grass', etc. (assuming lowercase in JSON)
        },

        ensureCardState(card) {
            if (!card) return;
            if (!Array.isArray(card.attachedEnergies)) card.attachedEnergies = [];
            if (typeof card.damageMarkers !== 'number') card.damageMarkers = 0;
            if (!card.status) card.status = null; // e.g., 'asleep','paralyzed','poisoned','burned'
        },

        attachEnergyTo(card, type) {
            if (!card || !type) return false;
            this.ensureCardState(card);
            card.attachedEnergies.push(this.normalizeEnergySymbol(type));
            return true;
        },

        countEnergies(card) {
            this.ensureCardState(card);
            const counts = {total: card.attachedEnergies.length};
            for (const e of card.attachedEnergies) {
                counts[e] = (counts[e] || 0) + 1;
            }
            return counts;
        },

        canPayCost(card, costArr) {
            console.log(card, costArr)

            if (!Array.isArray(costArr) || costArr.length === 0) return true;

            const counts = this.countEnergies(card);

            let colorlessNeeded = 0;
            const typedNeeds = {};

            for (const c of costArr) {
                const t = this.normalizeEnergySymbol(c);
                if (t === 'colorless') colorlessNeeded++; else typedNeeds[t] = (typedNeeds[t] || 0) + 1;
            }
            // satisfy typed first
            for (const [t, n] of Object.entries(typedNeeds)) {
                if ((counts[t] || 0) < n) return false;
            }
            const usedTyped = Object.values(typedNeeds).reduce((a, b) => a + b, 0);
            const remaining = counts.total - usedTyped;
            return remaining >= colorlessNeeded;
        },

        // Weakness / Resistance
        applyWeaknessResistance(attacker, defender, dmg) {
            if (!defender || !attacker) {
                return dmg;
            }

            let result = dmg;
            const atkTypes = (attacker.types || []).map(t => String(t).toLowerCase());
            const weaknesses = defender.weaknesses || [];

            for (const w of weaknesses) {
                const wType = String(w?.type || '').toLowerCase();

                if (wType && atkTypes.includes(wType)) {
                    const multStr = String(w?.value || '×2');
                    const mult = multStr.includes('×') ? parseFloat(multStr.replace('×', '')) : 2;
                    if (!isNaN(mult)) result = Math.floor(result * mult);
                }
            }

            const resistances = defender.resistances || [];

            for (const r of resistances) {
                const rType = String(r?.type || '').toLowerCase();

                if (rType && atkTypes.includes(rType)) {
                    const minus = parseInt(String(r?.value || '-30').replace(/[^0-9-]/g, ''), 10);
                    if (!isNaN(minus)) result = Math.max(result - Math.abs(minus), 0);
                }
            }

            return result;
        },

        // === Deck helpers ===
        async loadUserDeck(userDeckId) {
            if (!userDeckId) {
                return null;
            }

            const res = await axios.get(`/api/user-decks/${userDeckId}`);
            const ud = res.data;
            const stack = [];

            for (const entry of (ud?.cards || [])) {
                const model = entry.card;

                if (!model) {
                    continue;
                }

                let cj = model.json;

                if (typeof cj === 'string') {
                    try {
                        cj = JSON.parse(cj);
                    } catch (_) {
                        cj = null;
                    }
                }

                if (!cj) {
                    continue;
                }

                const qty = Number(entry.quantity || 1);

                for (let i = 0; i < qty; i++) {
                    stack.push(structuredClone(cj));
                }
            }

            this.shuffle(stack);

            return stack;
        },

        shuffle(arr) {
            for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }

            return arr;
        },

        // === Start rules helpers ===
        isBasicPokemon(card) {
            if (!card) {
                return false;
            }

            const supertype = String(card.supertype || '').toLowerCase();

            if (supertype !== 'pokémon' && supertype !== 'pokemon') {
                return false;
            }

            const subs = (card.subtypes || []).map(s => String(s).toLowerCase());

            return subs.includes('basic');
        },

        drawN(side, n, toHand = true) {
            const drawn = [];

            for (let i = 0; i < n; i++) {
                if (!side.deck.length) {
                    break;
                }

                const c = side.deck.shift();

                if (toHand) {
                    side.handCards.push(c);
                }

                drawn.push(c);
            }

            return drawn;
        },

        pickBasicsFromHand(side, count) {
            const basics = side.handCards.filter(c => this.isBasicPokemon(c));

            return basics.slice(0, count);
        },

        setupOpeningForSide(side) {
            // Returns mulligan count
            let mulligans = 0;

            // Ensure hand and other zones are reset
            side.handCards = side.handCards || [];
            side.activeCard = null;
            side.bank = [];

            // Mulligan loop
            let safety = 0;

            while (safety < 20) {
                safety++;

                // Draw up to 7 cards
                this.drawN(side, 7 - side.handCards.length);
                const basics = side.handCards.filter(c => this.isBasicPokemon(c));

                if (basics.length === 0) {
                    // Mulligan: shuffle hand back into deck and draw again
                    side.deck = side.deck.concat(side.handCards);
                    side.handCards = [];
                    this.shuffle(side.deck);
                    mulligans++;
                    continue;
                }

                // Place Active and up to 3 Bench from hand
                const chosen = this.pickBasicsFromHand(side, 4);
                side.activeCard = chosen.shift();

                if (side.activeCard) {
                    // remove from hand
                    const idx = side.handCards.indexOf(side.activeCard);
                    if (idx >= 0) side.handCards.splice(idx, 1);
                    this.ensureCardState(side.activeCard);
                }

                side.bank = [];

                for (const b of chosen) {
                    const i = side.handCards.indexOf(b);

                    if (i >= 0) side.handCards.splice(i, 1);

                    this.ensureCardState(b);
                    side.bank.push(b);
                }

                this.announce(`${side.name} set Active and ${side.bank.length} Bench Pokémon`);
                break;
            }

            return mulligans;
        },

        async setupGameFromDecks(playerDeck, opponentDeck) {
            // Initialize sides with decks
            this.player.deck = Array.isArray(playerDeck) ? [...playerDeck] : [];
            this.opponent.deck = Array.isArray(opponentDeck) ? [...opponentDeck] : [];

            this.shuffle(this.player.deck);
            this.shuffle(this.opponent.deck);

            this.player.handCards = [];
            this.opponent.handCards = [];
            this.player.prizeCards = [];
            this.opponent.prizeCards = [];
            this.player.discardPile = [];
            this.opponent.discardPile = [];

            // Opponent automated setup (for now)
            const oM = this.setupOpeningForSide(this.opponent);
            this.opponentMulligans = oM;

            if (oM > 0) {
                this.announce(`CPU mulliganed ${oM} time(s)`, 'warning');
            }

            this.opponent.prizeCards = this.drawN(this.opponent, 3, false);

            // Per rules: you draw 1 for each time your opponent mulliganed
            if (this.opponentMulligans > 0) {
                this.drawN(this.player, this.opponentMulligans);
                this.announce(`You draw ${this.opponentMulligans} extra card(s) due to opponent's mulligan.`, 'info');
            }

            // Player: draw 7, mulligan until at least one Basic is in hand
            this.playerMulligans = 0;
            this.drawN(this.player, 7);
            let safety = 0;

            while (safety < 20) {
                safety++;
                const basics = this.player.handCards.filter(c => this.isBasicPokemon(c));

                if (basics.length > 0) {
                    break;
                }

                // Mulligan: shuffle hand back in, reshuffle, redraw 7
                this.player.deck = this.player.deck.concat(this.player.handCards);
                this.player.handCards = [];
                this.shuffle(this.player.deck);
                this.drawN(this.player, 7);
                this.playerMulligans++;
                this.announce('You have no Basic Pokémon. Mulligan and redraw 7.', 'warning');
            }

            // Per rules: opponent draws 1 for each time you mulliganed
            if (this.playerMulligans > 0) {
                this.drawN(this.opponent, this.playerMulligans);
                this.announce(`CPU draws ${this.playerMulligans} extra card(s) due to your mulligan.`, 'info');
            }

            // Reset any manual setup selection helpers (kept for future UI)
            this.setupSelectedActive = null;
            this.setupSelectedBenchIds = [];

            // Set player prize cards now and start the game flow
            this.player.prizeCards = this.drawN(this.player, 3, false);

            // Decide who starts by coin flip
            const coin = Math.random() < 0.5 ? 'heads' : 'tails';
            const starts = coin === 'heads' ? this.player : this.opponent;
            this.announce(`Coin flip: ${coin}. ${starts.name} goes first.`, 'info');
            this.startingPlayer = starts;
            this.currentAttacker = starts;
            this.currentDefender = (starts === this.player) ? this.opponent : this.player;

            // Allow the player to place Pokémon by drag & drop immediately
            this.announce('Drag a Basic Pokémon from your hand to the Active or Bench slots to begin.', 'info');

            // Start the first turn automatically (auto-draw handled in startTurn)
            this.startTurn();
        },

        showActiveCardActions() {
            if (!this.player.activeCard) return;
            this.cardToShow = this.player.activeCard;
        },

        showCard(card) {
            if (!card) return;
            this.cardToShow = card;
        },

        async executeAction(action) {
            console.log(action)

            if (this.isResolving) {
                return;
            }

            if (this.currentPhase !== 'attack') {
                this.announce('You can only attack in the attack phase', 'info');
                return;
            }

            // First turn rule: the player who goes first cannot attack on the very first turn
            if (this.turnCount === 1 && this.currentAttacker === this.startingPlayer) {
                this.announce('You cannot attack on the first turn if you go first', 'warning');
                return;
            }

            const attacker = this.currentAttacker?.activeCard;

            if (!attacker) {
                return;
            }

            if (!this.canPayCost(attacker, action?.cost || [])) {
                this.announce('Not enough energy to use this attack', 'error');
                return;
            }

            this.isResolving = true;
            this.cardToShow = null;
            this.announce((this.currentAttacker?.activeCard?.name || 'Attacker') + ' uses ' + (action?.name || 'an attack'), 'info')

            if (action && action.damage !== undefined) {
                const base = parseInt(String(action.damage || '0').replace('+', '').trim(), 10) || 0;
                let effectiveDamage = base;

                if (action.type === 'damage-increasable' && action.dependency) {
                    const parts = String(action.dependency).split(':');
                    const condition = parts[0];
                    const value = parts[1];

                    if (condition === 'opponent-retreat-cost') {
                        (this.currentDefender?.activeCard?.retreatCost || []).forEach(rC => {
                            if (rC === value) {
                                effectiveDamage = effectiveDamage + Number(action.increaseBy || 0);
                            }
                        });
                    }

                    if (condition === 'coin') {
                        const times = parseInt(parts[2] || '1', 10) || 1;

                        for (let i = 0; i < times; i++) {
                            let coinResult = await this.flipCoin()
                            if (coinResult === value) {
                                effectiveDamage = effectiveDamage + Number(action.step || 0)
                            }
                        }
                    }

                    if (condition === 'coin-until') {
                        const step = Number(action.step || 0);
                        let done = false;

                        while (!done) {
                            let coinResult = await this.flipCoin()

                            if (coinResult === value) {
                                done = true;
                            } else {
                                effectiveDamage = effectiveDamage + step
                            }
                        }
                    }

                    if (condition === 'coin-ko') {
                        const times = parseInt(parts[2] || '2', 10) || 2;
                        let coinResults = [];

                        for (let i = 0; i < times; i++) {
                            coinResults.push(await this.flipCoin())
                        }

                        if (coinResults.filter(r => r === value).length === times) {
                            const def = this.currentDefender?.activeCard;
                            const remaining = (parseInt(def?.hp, 10) || 0) - ((def?.damageMarkers || 0) * 10);
                            effectiveDamage = Math.max(remaining, 0);
                            this.announce('KO!', 'error')
                        }
                    }
                }

                if (effectiveDamage > 0) {
                    const def = this.currentDefender.activeCard;
                    effectiveDamage = this.applyWeaknessResistance(attacker, def, effectiveDamage);
                    def.damageMarkers = (def.damageMarkers ?? 0) + (effectiveDamage / 10);
                    this.announce('Dealt ' + effectiveDamage + ' damage to opponent\'s ' + def.name)
                } else {
                    this.announce('No effect', 'info');
                }

                if ((this.currentDefender.activeCard.damageMarkers * 10) >= (parseInt(this.currentDefender.activeCard.hp, 10) || 0)) {
                    this.announce(this.currentDefender.activeCard.name + ' has been defeated!')

                    // Move KO'd Pokémon to discard
                    this.currentDefender.discardPile.push(this.currentDefender.activeCard);
                    // Auto-promote from bench
                    const next = this.currentDefender.bank.shift();
                    this.currentDefender.activeCard = next || null;

                    // Attacker takes a prize and check win conditions
                    this.takePrize(this.currentAttacker);
                    this.checkWinConditions();
                }

                // After attacking, move to end phase; turn swap happens via End Turn button
                this.currentPhase = 'end';

                this.$forceUpdate();
            }
            this.isResolving = false;
        },

        flipCoin() {
            let options = ['heads', 'tails'];

            return new Promise((resolve) => {
                setTimeout(() => {
                    let result = options[Math.floor(Math.random() * options.length)];
                    this.announce('Coin flip: ' + result);
                    resolve(result);
                }, 600);
            });
        },

        announce(body, type = 'success') {
            iziToast[type]({
                title: body,
                timeout: 3000,
            });
        },

        openLightbox(card) {
            this.lightboxCard = card;
        },

        isEnergyCard(card) {
            const st = (card?.supertype || card?.superType || '').toLowerCase();

            if (st === 'energy') {
                return true;
            }

            // Fallback: name contains "Energy"
            return String(card?.name || '').toLowerCase().includes('energy');
        },

        getEnergyTypeFromCard(card) {
            // Heuristic mapping based on name/types
            const name = String(card?.name || '').toLowerCase();
            const t = (Array.isArray(card?.types) && card.types.length) ? String(card.types[0]).toLowerCase() : null;
            const map = ['fire', 'water', 'grass', 'lightning', 'psychic', 'fighting', 'metal', 'darkness', 'fairy', 'dragon', 'colorless'];

            for (const k of map) {
                if (name.includes(k)) return k;
            }

            if (t && map.includes(t)) {
                return t;
            }

            return 'colorless';
        },

        onGameTapTarget(target, benchIndex = null) {
            // If a touch selection exists, reuse the existing drop logic
            if (!this.touchSelect) {
                return;
            }

            this.dragPayload = {...this.touchSelect};
            this.touchSelect = null;
            this.onGameDropTo(target, benchIndex);
        },

        onGameDropTo(target, benchIndex = null) {
            const payload = this.dragPayload || {};
            const card = payload.card;

            if (!card) {
                return;
            }

            // Reset payload early to avoid accidental re-use
            this.dragPayload = {card: null, from: null, index: null};

            // Only allow the player to perform placements during their main phase
            if (this.currentAttacker !== this.player || this.currentPhase !== 'main') {
                this.announce('You can only play cards in your main phase.', 'warning');
                return;
            }

            // Dropping on ACTIVE spot
            if (target === 'active') {
                // Energy attachment to active
                if (this.isEnergyCard(card)) {
                    if (this.flags.energyAttachedThisTurn) {
                        this.announce('You already attached an Energy this turn.', 'warning');
                        return;
                    }

                    if (!this.player.activeCard) {
                        this.announce('No Active Pokémon to attach Energy to.', 'warning');
                        return;
                    }

                    const eType = this.getEnergyTypeFromCard(card);
                    this.attachEnergyTo(this.player.activeCard, eType);
                    this.flags.energyAttachedThisTurn = true;

                    // remove from hand
                    this.player.handCards.splice(payload.index, 1);
                    this.announce('Attached ' + eType + ' Energy to Active.');
                    return;
                }

                // Play a Basic Pokémon to Active only if empty
                if (!this.player.activeCard && this.isBasicPokemon(card)) {
                    // remove from hand and set active
                    this.player.handCards.splice(payload.index, 1);
                    this.player.activeCard = card;
                    this.ensureCardState(card);
                    this.announce('Set Active: ' + (card.name || 'Pokémon'));
                    return;
                }

                this.announce('Cannot place this card to Active.', 'warning');
                return;
            }

            // Dropping on BENCH spot
            if (target === 'bench') {
                // Attaching energy to bench Pokémon if slot occupied
                if (benchIndex !== null && this.player.bank[benchIndex]) {
                    if (this.isEnergyCard(card)) {
                        if (this.flags.energyAttachedThisTurn) {
                            this.announce('You already attached an Energy this turn.', 'warning');
                            return;
                        }
                        const eType = this.getEnergyTypeFromCard(card);
                        this.attachEnergyTo(this.player.bank[benchIndex], eType);
                        this.flags.energyAttachedThisTurn = true;
                        this.player.handCards.splice(payload.index, 1);
                        this.announce('Attached ' + eType + ' Energy to Bench.');
                        return;
                    }
                }

                // Playing a Basic Pokémon from hand to a bench slot
                if (this.isBasicPokemon(card)) {
                    if (benchIndex === null) benchIndex = 0;
                    if (benchIndex < 0 || benchIndex > 2) benchIndex = 0;
                    if (this.player.bank[benchIndex]) {
                        this.announce('Bench slot is occupied.', 'warning');
                        return;
                    }
                    this.player.handCards.splice(payload.index, 1);
                    this.ensureCardState(card);
                    this.$set ? this.$set(this.player.bank, benchIndex, card) : (this.player.bank[benchIndex] = card);
                    this.announce('Placed on Bench: ' + (card.name || 'Pokémon'));
                    return;
                }

                this.announce('Cannot place this card on Bench.', 'warning');
                return;
            }
        },
        // === Prizes & Win Conditions ===
        takePrize(side) {
            if (!side?.prizeCards) return;
            if (side.prizeCards.length > 0) {
                const prize = side.prizeCards.pop();
                side.handCards.push(prize);
                this.announce(side.name + ' took a Prize card');
            }
        },

        hasAnyPokemon(side) {
            return !!(side?.activeCard || (side?.bank && side.bank.length > 0));
        },

        checkWinConditions() {
            // Win if attacker has taken all prizes
            if ((this.currentAttacker?.prizeCards?.length ?? 0) === 0) {
                this.announce(this.currentAttacker.name + ' wins by taking all Prize cards!', 'success');
                this.isResolving = true;
                return true;
            }
            // Win if defender has no Pokémon left on board
            if (!this.hasAnyPokemon(this.currentDefender)) {
                this.announce(this.currentAttacker.name + ' wins! Opponent has no Pokémon left!', 'success');
                this.isResolving = true;
                return true;
            }
            return false;
        },

        // === Simple CPU automation ===
        delay(ms) {
            return new Promise(res => setTimeout(res, ms));
        },
        cpuEnsureActive() {
            const side = this.opponent;
            if (side.activeCard) return;
            const idx = side.handCards.findIndex(c => this.isBasicPokemon(c));
            if (idx >= 0) {
                const card = side.handCards.splice(idx, 1)[0];
                this.ensureCardState(card);
                side.activeCard = card;
                this.announce('CPU set Active: ' + (card.name || 'Pokémon'));
            }
        },

        cpuFillBench() {
            const side = this.opponent;

            // fill up to 3 bench slots
            for (let i = 0; i < 3; i++) {
                if (side.bank[i]) continue;
                const idx = side.handCards.findIndex(c => this.isBasicPokemon(c));
                if (idx < 0) break;
                const card = side.handCards.splice(idx, 1)[0];
                this.ensureCardState(card);
                this.$set ? this.$set(side.bank, i, card) : (side.bank[i] = card);
                this.announce('CPU placed on Bench: ' + (card.name || 'Pokémon'));
            }
        },

        cpuAttachEnergy() {
            // Attach first energy from hand to Active, else first Bench
            if (this.flags.energyAttachedThisTurn) return false;
            const side = this.opponent;
            const idx = side.handCards.findIndex(c => this.isEnergyCard(c));
            if (idx < 0) return false;
            const energyCard = side.handCards.splice(idx, 1)[0];
            const eType = this.getEnergyTypeFromCard(energyCard);
            const target = side.activeCard || side.bank[0] || null;
            if (!target) return false;
            this.attachEnergyTo(target, eType);
            this.flags.energyAttachedThisTurn = true;
            this.announce('CPU attached ' + eType + ' Energy');
            return true;
        },

        cpuChooseAttack() {
            const side = this.opponent;
            const active = side.activeCard;
            if (!active) return null;
            const attacks = Array.isArray(active.attacks) ? active.attacks : [];
            // Prefer strongest attack we can pay
            const payable = attacks.filter(a => this.canPayCost(active, a?.cost || []));
            if (!payable.length) return null;
            payable.sort((a, b) => {
                const da = parseInt(String(a?.damage || '0').replace('+', '').trim(), 10) || 0;
                const db = parseInt(String(b?.damage || '0').replace('+', '').trim(), 10) || 0;
                return db - da;
            });
            return payable[0];
        },

        async cpuTakeTurn() {
            // Guard: stop if already resolving or game over
            if (this.isResolving) return;
            // CPU orchestration: draw, main plays, attack, end turn
            try {
                // Start phase: draw one
                await this.delay(350);
                if (this.currentPhase === 'start' && this.currentAttacker === this.opponent) {
                    this.drawOne(this.opponent);
                    this.currentPhase = 'main';
                } else {
                    // If not in start, ensure phase is main to proceed
                    this.currentPhase = 'main';
                }
                await this.delay(350);

                // Main phase plays
                this.cpuEnsureActive();
                await this.delay(300);
                this.cpuFillBench();
                await this.delay(300);
                this.cpuAttachEnergy();
                await this.delay(300);

                // Move to attack phase
                this.currentPhase = 'attack';
                await this.delay(350);

                // Choose and execute attack if allowed (respect first turn rule)
                const cannotAttackFirstTurn = (this.turnCount === 1 && this.currentAttacker === this.startingPlayer);
                if (!cannotAttackFirstTurn) {
                    const action = this.cpuChooseAttack();
                    if (action) {
                        await this.executeAction(action);
                    } else {
                        this.announce('CPU skips attack');
                        this.currentPhase = 'end';
                    }
                } else {
                    this.announce('CPU cannot attack on the first turn.');
                    this.currentPhase = 'end';
                }

                await this.delay(300);
                // End turn
                this.endTurn();
            } catch (e) {
                console.error('CPU turn error', e);
                // Fail safe: end turn to avoid lock
                this.endTurn();
            }
        },
    },
};
</script>


<style scoped lang="scss">
.game-display {
    display: flex;
    flex-direction: column;
    margin-top: 6px !important;

    @media (max-width: 576px) {
        min-height: 100%;
        max-height: 100%;
        overflow: hidden;
        margin-top: 0 !important;
    }
}

.page-content {
    padding-top: 4px !important;
    max-height: 80vh;
    overflow: hidden;

    @media (max-width: 576px) {
        padding-top: 0 !important;
        padding-bottom: 60px !important;
        max-height: 80vh;
        overflow: hidden;
    }
}

.height-cap {
    max-height: 80vh;
    overflow: hidden;
}

.bank, .active-card-wrapper {
    display: flex;
    justify-content: center;
}

.deck {
    background-image: url(/assets/dipocaco/card_back_sm.png);
    background-size: cover;
    background-position: center center;
}

.deck-wrapper, .discard-pile-wrapper {
    justify-content: center;
}

.bank-card, .active-card, .deck, .discard-pile {
    width: 5.75em;
    height: auto;
    aspect-ratio: 63 / 88;
    border: 1px solid gray;
    border-radius: 5px;
    margin: 0.35em;
    background-color: rgba(211, 211, 211, 0.5);

    @media (max-width: 576px) {
        width: 4.2em;
        margin: 0.5em;
    }
}

.active-card {
    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }

    @media (max-width: 576px) {
        width: min(28vw, 200px) !important;
    }
}

.bank-card {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    @media (max-width: 576px) {
        width: 14vw;
        margin: 0.2em;
    }
}

.deck, .discard-pile {
    @media (max-width: 576px) {
        width: 10vw;
    }
}

.discard-pile {
    text-align: center;
}

.card-with-overlay {
    position: relative;
}

.hp-overlay {
    position: absolute;
    top: -10px;
    right: -6px;
    display: flex;
    align-items: center;
    gap: 6px;

    .hp-number {
        background: rgba(255, 255, 255, 0.95);
        color: #222;
        border-radius: 14px;
        padding: 2px 6px;
        font-weight: 800;
        font-size: 14px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
    }

    .hp-bar {
        width: 54px;
        height: 8px;
        background: #2b2b2b;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.6);
    }

    .hp-fill {
        height: 100%;
        background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
    }

    @media (max-width: 576px) {
        top: -6px;
        right: -4px;
        gap: 3px;

        .hp-number {
            font-size: 11px;
            padding: 2px 4px;
        }

        .hp-bar {
            width: 42px;
            height: 6px;
        }
    }
}

.prize-dots {
    display: flex;
    justify-content: center;
    align-items: center;

    .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.15);
        border: 1px solid rgba(0, 0, 0, 0.25);

        &.filled {
            background: #4b5563;
            border-color: #111827;
        }
    }
}

.placeholder-text {
    display: flex;
    height: 100%;
    align-content: center;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: #444444;

    @media (max-width: 576px) {
        font-size: 0.8em;
    }
}

.player-area {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    flex-direction: row;
    transition: filter .2s ease;

    .player {
        &.player-area {
            margin-top: 2em;

            @media (max-width: 576px) {
                margin-top: 0.3em;
            }
        }
    }
}

#card-action-view {
    z-index: 200;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);

    .actions-card-image {
        width: 24em;
        margin: 4em auto;
        display: block;
    }

    .card-action {
        width: 30em;
        min-height: 6em;
        border: 3px solid black;
        background-color: #ffffff;
        margin: 0 auto 1em;
        border-radius: 2px;
        box-shadow: 4px 4px rgba(112, 128, 144, 0.5);

        &.disabled-action {
            opacity: 0.5;
            pointer-events: none;
            filter: grayscale(60%);
        }

        .action-energy-cost {
            display: inline-block;
            padding: 0.5em;
            width: 25%;
            vertical-align: top;

            img {
                width: 33%;
                display: inline-block;
                padding: 0.25em;
            }
        }

        .action-info {
            display: inline-block;
            padding: 0.5em;
            width: 75%;
            color: black !important;

            .action-name {
                font-size: 1.25em;
                font-weight: bold;
            }

            .action-damage {
                font-size: 1.25em;
                float: right;
                font-weight: bold;
            }
        }
    }
}

.turn-label {
    &.you {
        color: #2ecc71;
        font-weight: 700;
    }

    &.cpu {
        color: #e74c3c;
        font-weight: 700;
    }
}

.status-sidebar {
    position: fixed;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 110px;
    max-width: 110px;
    z-index: 50;

    @media (max-width: 576px) {
        right: 6px;
        width: 100px;
        max-width: 100px;
    }
}

.hand {
    @media (max-width: 576px) {
        padding: 0 !important;
    }

    .hand-card {
        height: 80px;
        width: auto;
        margin: 2px;

        &.card-back {
            @media (max-width: 576px) {
                height: 40px;
                width: 30px;
            }
        }

        @media (max-width: 576px) {
            width: 36px;
        }
    }

    &.enemy {
        position: relative;
        height: 70px;

        @media (max-width: 576px) {
            height: 44px;
        }
    }
}

.hand-cards {
    gap: 4px;
    padding: 2px 2px;

    &.enemy {
        &.overlapped {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: 50px;
            pointer-events: none;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            z-index: 40;

            .hand-card.card-back {
                position: relative;

                + .hand-card.card-back {
                    margin-left: -14px;
                }
            }

            @media (max-width: 576px) {
                height: 44px;
            }
        }
    }

    @media (max-width: 576px) {
        gap: 0 !important;
        padding: 0 !important;
    }
}

.toolbar.minimal {
    position: sticky;
    top: 0;
    z-index: 50;
}

.drag-ghost {
    position: fixed;
    left: 0;
    top: 0;
    transform: translate(-50%, -50%);
    z-index: 9999;
    pointer-events: none;

    img {
        width: 180px;
        height: auto;
        border-radius: 6px;
        filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.45));

        @media (max-width: 576px) {
            width: 140px;
        }
    }
}
</style>